/**
 * Gets the Shopify App Bridge session token.
 *
 * App Bridge v4 injects `window.shopify` automatically when running inside
 * the Shopify Admin iframe. We retry with backoff to handle the brief window
 * on first load before App Bridge has fully initialised.
 *
 * The token is cached for 55 seconds — Shopify tokens are valid for 60s,
 * so we refresh slightly early to avoid race conditions.
 */

let _cachedToken: string | null = null
let _cacheExpiry = 0
// Track an in-flight fetch so concurrent calls don't each trigger their own retry loop
let _inflight: Promise<string> | null = null

async function fetchToken(): Promise<string> {
  const delays = [0, 50, 150, 400, 800, 1500, 3000]

  for (const delay of delays) {
    if (delay > 0) await new Promise((r) => setTimeout(r, delay))

    // Embedded apps always run inside the Shopify Admin iframe. On a top-level
    // page (the marketing site, /support, /docs) App Bridge is never going to
    // appear, so walking the full backoff just stalls the first tRPC call by
    // ~6s. Bail as soon as we know we're not embedded.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (window.top === window.self && !(window as any).shopify) return ''

    try {
      // App Bridge v4: window.shopify.idToken()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const shopify = (window as any).shopify
      if (shopify?.idToken) {
        // Race against a 3s timeout — idToken() can hang if App Bridge postMessage
        // handshake with Shopify admin is still in progress.
        const token = await Promise.race<string>([
          shopify.idToken(),
          new Promise<string>((resolve) => setTimeout(() => resolve(''), 3000)),
        ])
        if (token) return token as string
      }

      // App Bridge v3 / legacy fallback
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const legacy = (window as any).__SHOPIFY_APP_BRIDGE__
      if (legacy?.getSessionToken) {
        const token = await legacy.getSessionToken()
        if (token) return token as string
      }
    } catch {
      // Continue retrying
    }
  }

  return ''
}

/**
 * Drops the cached token so the next call mints a fresh one.
 * Used when the server rejects a token (401) — the cached copy may have
 * expired in flight, or App Bridge may have re-established its session.
 */
export function clearShopifySessionTokenCache(): void {
  _cachedToken = null
  _cacheExpiry = 0
}

export async function getShopifySessionToken(): Promise<string> {
  if (typeof window === 'undefined') return ''

  // Return cached token if still valid
  if (_cachedToken && Date.now() < _cacheExpiry) return _cachedToken

  // Deduplicate concurrent calls — all callers await the same fetch
  if (!_inflight) {
    _inflight = fetchToken().then((token) => {
      if (token) {
        _cachedToken = token
        _cacheExpiry = Date.now() + 55_000 // cache for 55s (tokens valid for 60s)
      }
      _inflight = null
      return token
    })
  }

  return _inflight
}
